import React from 'react';
import { cn } from '@/lib/utils';

export type TypographyVariant =
    | 'heading-large'
    | 'heading-small'
    | 'label'
    | 'body'
    | 'caption';

export interface TypographyProps extends React.HTMLAttributes<HTMLSpanElement> {
	variant?: TypographyVariant;
	color?: string;
	className?: string;
}

const typographyStyles = {
	'heading-large': cn(
		// Base styles for heading-large
		'font-[500] font-lora',
		// Responsive sizes
		'text-[22px] md:text-[26px] lg:text-[32px]'
	),
	'heading-small': cn(
		// Base styles for heading-small
		'font-[500] leading-tight font-lora',
		// Responsive sizes
		'text-[20px] md:text-[22px] lg:text-[28px] min-[440px]:max-[639px]:text-[28px]'
	),
	label: cn(
		// Base styles for label
		'font-[600] font-poppins',
		// Responsive sizes
		'text-[14px] md:text-[16px] lg:text-[18px] min-[440px]:max-[639px]:text-[18px]'
	),
	body: cn(
		// Base styles for body
		'font-[400] font-poppins',
		// Responsive sizes
		'text-[14px] md:text-[16px] lg:text-[18px]'
	),
	caption: cn(
		// Base styles for caption
		'font-[400] font-poppins',
		// Responsive sizes
		'text-[12px] md:text-[14px] lg:text-[16px]'
	)
};

const Typography = React.forwardRef<HTMLSpanElement, TypographyProps>(
	(
		{
			variant = 'body',
			color,
			className,
			children,
			...props
		},
		ref
	) => {
		const baseStyles = cn(
			typographyStyles[variant],
			variant === 'label'
				? color
					? `text-[${color}]`
					: 'text-[var(--tab-foreground)]'
				: color
					? `text-[${color}]`
					: 'text-[var(--foreground)]',
			className
		);

		return (
			<span ref={ref} className={baseStyles} {...props}>
				{children}
			</span>
		);
	}
);

Typography.displayName = 'Typography';

export { Typography };